// OrdersControl.js
import React, { useState, useEffect } from 'react';
import { useControlAuth } from '../../ControlAuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { FiDownload, FiEye, FiPlus, FiSearch, FiTrash2 } from 'react-icons/fi';
import orderExportTemplate from '../../assets/control-orders-template.xlsx';
import { populateOrdersTemplate } from '../../utils/controlOrdersExcel';
import './OrdersControl.css';

const OrdersControl = () => {
  const { user } = useControlAuth();
  const [orders, setOrders] = useState([]);
  const [sortField, setSortField] = useState('creation_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('controlToken');
      try {
        const response = await axios.get('https://valleyapi-dr9y.onrender.com/api/control/orders', {
          headers: { Authorization: `Bearer ${token}` }
        });
        let fetchedOrders = response.data.orders || [];
        if (user.accesstype === 1) {
          fetchedOrders = fetchedOrders.filter(order => order.owner === user.userid);
        }
        setOrders(fetchedOrders);
      } catch (err) {
        setError('Failed to fetch orders');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (user) {
      fetchOrders();
    }
  }, [user]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredOrders = orders.filter(order => {
    const creationDateStr = new Date(order.creation_date).toLocaleDateString();
    const priceStr = `$${order.price}`;
    const query = searchTerm.toLowerCase();
    return (
      String(order.orderid || '').toLowerCase().includes(query) ||
      String(order.owner || '').toLowerCase().includes(query) ||
      creationDateStr.includes(searchTerm) ||
      String(order.status || '').toLowerCase().includes(query) ||
      priceStr.includes(searchTerm)
    );
  });

  const sortedOrders = [...filteredOrders].sort((a, b) => {
    let valA = a[sortField];
    let valB = b[sortField];
    if (sortField === 'creation_date') {
      valA = new Date(valA);
      valB = new Date(valB);
    } else if (sortField === 'price') {
      valA = parseFloat(valA);
      valB = parseFloat(valB);
    }
    if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
    if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSelect = (orderNumber) => {
    setSelectedOrders(prev =>
      prev.includes(orderNumber)
        ? prev.filter(id => id !== orderNumber)
        : [...prev, orderNumber]
    );
  };

  const handleSelectAll = () => {
    const visibleIds = sortedOrders.map(order => order.order_number);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedOrders.includes(id));
    if (allVisibleSelected) {
      setSelectedOrders(previous => previous.filter(id => !visibleIds.includes(id)));
    } else {
      setSelectedOrders(previous => [...new Set([...previous, ...visibleIds])]);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete selected orders?')) {
      const token = localStorage.getItem('controlToken');
      try {
        await axios.post('https://valleyapi-dr9y.onrender.com/api/control/orders/delete', { order_numbers: selectedOrders }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOrders(orders.filter(order => !selectedOrders.includes(order.order_number)));
        setSelectedOrders([]);
      } catch (err) {
        setError('Failed to delete orders');
        console.error(err);
      }
    }
  };

  // ────────────────────────────────────────────────
  //           EXPORTAR A EXCEL (las órdenes visibles)
  // ────────────────────────────────────────────────
  const exportToExcel = async () => {
    if (sortedOrders.length === 0) {
      alert('No hay órdenes para exportar');
      return;
    }

    setExporting(true);
    try {
      const templateResponse = await fetch(orderExportTemplate);
      if (!templateResponse.ok) throw new Error('Excel template could not be loaded');
      const templateBytes = await templateResponse.arrayBuffer();
      const excelBuffer = populateOrdersTemplate(templateBytes, sortedOrders);
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(data, `Valley_Orders_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (exportError) {
      console.error(exportError);
      alert('No se pudo generar el Excel. Intenta nuevamente.');
    } finally {
      setExporting(false);
    }
  };

  const getSortArrow = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const visibleOrdersSelected = sortedOrders.length > 0
    && sortedOrders.every(order => selectedOrders.includes(order.order_number));

  if (loading) return <div className="oc-state"><span className="oc-loader" />Loading orders…</div>;
  if (error) return <div className="oc-state is-error">{error}</div>;

  return (
    <div className="oc-container">
      <header className="oc-header">
        <div><span>Order management</span><h1>Orders</h1><p>Review, export and manage the current order pipeline.</p></div>
        <div className="oc-actions">
          <Link to="/control/orders-control/create" className="oc-button oc-create"><FiPlus />Create order</Link>
          <button onClick={exportToExcel} className="oc-button oc-export" disabled={sortedOrders.length === 0 || exporting}>
            <FiDownload />{exporting ? 'Preparing Excel…' : 'Download Excel'}
          </button>
          {user.accesstype === 0 && selectedOrders.length > 0 && (
            <button onClick={handleDelete} className="oc-button oc-delete"><FiTrash2 />Delete selected</button>
          )}
        </div>
      </header>

      <div className="oc-toolbar">
        <label className="oc-search"><FiSearch /><input type="search" placeholder="Search by ID, owner, status or date…" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} /></label>
        <span>{sortedOrders.length} visible {sortedOrders.length === 1 ? 'order' : 'orders'}</span>
      </div>

      <div className="oc-table-wrap"><table className="oc-table">
        <thead>
          <tr>
            {user.accesstype === 0 && (
              <th>
                <input
                  type="checkbox"
                  checked={visibleOrdersSelected}
                  onChange={handleSelectAll}
                />
              </th>
            )}
            <th onClick={() => handleSort('orderid')} className="oc-sortable">
              ID de la orden {getSortArrow('orderid')}
            </th>
            <th onClick={() => handleSort('owner')} className="oc-sortable">
              Owner {getSortArrow('owner')}
            </th>
            <th onClick={() => handleSort('creation_date')} className="oc-sortable">
              Fecha creación {getSortArrow('creation_date')}
            </th>
            <th onClick={() => handleSort('status')} className="oc-sortable">
              Estado {getSortArrow('status')}
            </th>
            <th onClick={() => handleSort('price')} className="oc-sortable">
              Precio {getSortArrow('price')}
            </th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {sortedOrders.map(order => (
            <tr key={order.order_number}>
              {user.accesstype === 0 && (
                <td>
                  <input
                    type="checkbox"
                    checked={selectedOrders.includes(order.order_number)}
                    onChange={() => handleSelect(order.order_number)}
                  />
                </td>
              )}
              <td>{order.orderid}</td>
              <td>{order.owner}</td>
              <td>{new Date(order.creation_date).toLocaleDateString('es-HN')}</td>
              <td><span className={`oc-status ${String(order.status || '').toLowerCase().includes('deliver') ? 'is-success' : String(order.status || '').toLowerCase().includes('pending') ? 'is-warning' : ''}`}>{order.status || 'Unspecified'}</span></td>
              <td>${Number(order.price || 0).toFixed(2)}</td>
              <td>
                <Link to={`/control/orders-control/${order.order_number}`} className="oc-details"><FiEye />Details</Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table></div>

      {sortedOrders.length === 0 && <div className="oc-empty"><strong>No orders found</strong><span>Adjust the search or create a new order.</span></div>}
    </div>
  );
};

export default OrdersControl;
